import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useBookmarks } from "../hooks/useBookmarks";
import { BookmarkCheck, X, BookOpen, Calendar, Building2, Network } from "lucide-react";

const TYPE_META = {
    topic: { label: "Investigation", Icon: Network },
    source: { label: "Source", Icon: BookOpen },
    event: { label: "Timeline event", Icon: Calendar },
    entity: { label: "Entity", Icon: Building2 },
};
const TYPES = ["topic", "entity", "source", "event"];

export default function SavedPage() {
    const { items, remove } = useBookmarks();
    const [filter, setFilter] = useState("all");
    const [authed, setAuthed] = useState(() => {
        try { return !!localStorage.getItem("ctx_token"); } catch { return false; }
    });

    useEffect(() => {
        // Keep banner copy in sync if the user signs in/out in another tab.
        const onStorage = (e) => {
            if (e.key === "ctx_token") {
                try { setAuthed(!!localStorage.getItem("ctx_token")); } catch { /* ignore */ }
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const filtered = useMemo(() => {
        if (filter === "all") return items;
        return items.filter((b) => b.type === filter);
    }, [items, filter]);

    return (
        <div className="min-h-screen bg-[#0B1220]">
            <Header />

            <section className="relative overflow-hidden border-b border-[#243044]">
                <div className="absolute inset-0 bg-grid opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/40 via-[#0B1220]/85 to-[#0B1220]" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 sm:pt-24 sm:pb-14">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#111827] border border-[#243044] mb-8">
                            <BookmarkCheck className="w-3 h-3 text-[#56CCF2]" />
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                                {authed ? "Saved · synced to your account" : "Saved · stored on this device"}
                            </span>
                        </div>
                        <h1 data-testid="saved-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6">
                            Your rabbit hole.
                        </h1>
                        <p className="text-lg text-[#94A3B8] leading-relaxed max-w-2xl">
                            {authed
                                ? "Investigations, entities, sources and timeline events you've bookmarked. Synced to your account — available wherever you sign in."
                                : "Investigations, entities, sources and timeline events you've bookmarked. Stored on this device for now. Sign in and we'll quietly move them with you."}
                        </p>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                <div className="flex flex-wrap gap-2">
                    <button
                        data-testid="saved-filter-all"
                        onClick={() => setFilter("all")}
                        className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition-colors border ${filter === "all" ? "bg-[#2F80ED] text-white border-[#2F80ED]" : "bg-[#111827] text-[#94A3B8] border-[#243044] hover:border-[#2F80ED]"}`}
                    >
                        All ({items.length})
                    </button>
                    {TYPES.map((t) => {
                        const count = items.filter((b) => b.type === t).length;
                        return (
                            <button
                                key={t}
                                data-testid={`saved-filter-${t}`}
                                onClick={() => setFilter(t)}
                                className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition-colors border ${filter === t ? "bg-[#2F80ED] text-white border-[#2F80ED]" : "bg-[#111827] text-[#94A3B8] border-[#243044] hover:border-[#2F80ED]"}`}
                            >
                                {TYPE_META[t].label} ({count})
                            </button>
                        );
                    })}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {filtered.length === 0 ? (
                    <div className="bg-[#111827] border border-[#243044] rounded-2xl p-12 text-center">
                        <BookmarkCheck className="w-8 h-8 text-[#56CCF2] mx-auto mb-4 opacity-60" />
                        <h3 className="text-xl font-semibold text-white mb-2">
                            {items.length === 0 ? "Nothing saved yet" : "No items match this filter"}
                        </h3>
                        <p className="text-sm text-[#94A3B8] mb-6 max-w-md mx-auto">
                            {items.length === 0
                                ? "Bookmark any investigation, entity, source or timeline event to save it here. Quiet, private, and stored only on this device."
                                : "Try a different filter or save more items."}
                        </p>
                        <Link to="/" className="inline-flex items-center text-sm text-[#56CCF2] hover:text-white transition-colors">
                            Browse the library →
                        </Link>
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {filtered.map((b, i) => {
                            const meta = TYPE_META[b.type] || TYPE_META.topic;
                            const Icon = meta.Icon;
                            return (
                                <li
                                    key={`${b.type}-${b.id}`}
                                    data-testid={`saved-item-${i}`}
                                    className="bg-[#111827] border border-[#243044] rounded-xl p-4 hover:border-[#2F80ED]/50 transition-colors flex items-start gap-4"
                                >
                                    <div className="w-10 h-10 rounded-lg bg-[#0B1220] border border-[#243044] flex items-center justify-center text-[#56CCF2] shrink-0">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1">{meta.label}</p>
                                        <Link
                                            to={b.href || "/"}
                                            className="text-base font-semibold text-white hover:text-[#56CCF2] leading-snug block transition-colors"
                                        >
                                            {b.title}
                                        </Link>
                                        {b.subtitle && (
                                            <p className="text-xs text-[#94A3B8] mt-1 line-clamp-1">{b.subtitle}</p>
                                        )}
                                        <p className="text-[10px] text-[#64748B] mt-2 uppercase tracking-wider">
                                            Saved {new Date(b.added_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                        </p>
                                    </div>
                                    <button
                                        data-testid={`saved-remove-${i}`}
                                        onClick={() => remove(b.type, b.id)}
                                        title="Remove from saved"
                                        className="text-[#64748B] hover:text-white p-1.5"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            <Footer />
        </div>
    );
}
